import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Save as SaveIcon,
  ArrowBack as BackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { reservationSettingsApi } from '../api/reservationApi';
import type { ReservationSettings as ReservationSettingsType } from '../types/reservation.types';
import toast from 'react-hot-toast';
import LoadingScreen from '../components/LoadingScreen';

const ReservationSettings: React.FC = () => {
  const navigate = useNavigate();
  const { user, isVendor } = useAuth();

  const [settings, setSettings] = useState<ReservationSettingsType | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Load settings
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const data = await reservationSettingsApi.get();
        setSettings(data);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    };

    if (user && isVendor) {
      loadSettings();
    }
  }, [user, isVendor]);

  const handleChange = (field: keyof ReservationSettingsType, value: any) => {
    if (!settings) return;
    setSettings({ ...settings, [field]: value });
  };

  const handleSave = async () => {
    if (!settings) return;

    setSaving(true);
    setError(null);

    try {
      const updated = await reservationSettingsApi.update(settings);
      setSettings(updated);
      toast.success('Settings saved successfully');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save settings');
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!user || !isVendor) {
    return (
      <Box className="p-6">
        <Alert severity="error">
          You don't have permission to access this page.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return <LoadingScreen message="Loading settings..." />;
  }

  if (!settings) {
    return (
      <Box className="p-6">
        <Alert severity="error">{error || 'Failed to load settings'}</Alert>
      </Box>
    );
  }

  return (
    <Box className="p-4 sm:p-6 max-w-4xl mx-auto">
      {/* Header */}
      <Box className="mb-6 flex items-center gap-4">
        <Button
          variant="text"
          startIcon={<BackIcon />}
          onClick={() => navigate('/vendor/reservations')}
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" className="font-bold">
            Reservation Settings
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Configure reservation system settings
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Feature Toggles */}
      <Card className="mb-6">
        <CardContent>
          <Typography variant="h6" className="mb-4">
            Feature Toggles
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.isReservationEnabled}
                    onChange={(e) =>
                      handleChange('isReservationEnabled', e.target.checked)
                    }
                  />
                }
                label="Enable Reservations"
              />
              <Typography variant="caption" color="textSecondary" display="block">
                Allow table reservations
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.isWaitlistEnabled}
                    onChange={(e) =>
                      handleChange('isWaitlistEnabled', e.target.checked)
                    }
                  />
                }
                label="Enable Waitlist"
              />
              <Typography variant="caption" color="textSecondary" display="block">
                Allow walk-in waitlist
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.isTableMergeEnabled}
                    onChange={(e) =>
                      handleChange('isTableMergeEnabled', e.target.checked)
                    }
                  />
                }
                label="Enable Table Merge"
              />
              <Typography variant="caption" color="textSecondary" display="block">
                Allow merging tables for large parties
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Time Settings */}
      <Card className="mb-6">
        <CardContent>
          <Typography variant="h6" className="mb-4">
            Time Settings
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Opening Time"
                type="time"
                fullWidth
                value={settings.openingTime}
                onChange={(e) => handleChange('openingTime', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Closing Time"
                type="time"
                fullWidth
                value={settings.closingTime}
                onChange={(e) => handleChange('closingTime', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Last Reservation Time"
                type="time"
                fullWidth
                value={settings.lastReservationTime}
                onChange={(e) => handleChange('lastReservationTime', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Default Duration (minutes)"
                type="number"
                fullWidth
                value={settings.defaultDurationMinutes}
                onChange={(e) =>
                  handleChange('defaultDurationMinutes', parseInt(e.target.value))
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Time Slot Interval (minutes)"
                type="number"
                fullWidth
                value={settings.timeSlotIntervalMinutes}
                onChange={(e) =>
                  handleChange('timeSlotIntervalMinutes', parseInt(e.target.value))
                }
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Booking Rules */}
      <Card className="mb-6">
        <CardContent>
          <Typography variant="h6" className="mb-4">
            Booking Rules
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Min Advance Booking (hours)"
                type="number"
                fullWidth
                value={settings.minAdvanceBookingHours}
                onChange={(e) =>
                  handleChange('minAdvanceBookingHours', parseInt(e.target.value))
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Max Advance Booking (days)"
                type="number"
                fullWidth
                value={settings.maxAdvanceBookingDays}
                onChange={(e) =>
                  handleChange('maxAdvanceBookingDays', parseInt(e.target.value))
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Max Party Size"
                type="number"
                fullWidth
                value={settings.maxPartySize}
                onChange={(e) =>
                  handleChange('maxPartySize', parseInt(e.target.value))
                }
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Require Phone for Party Size >"
                type="number"
                fullWidth
                value={settings.requirePhoneForLargeParty}
                onChange={(e) =>
                  handleChange('requirePhoneForLargeParty', parseInt(e.target.value))
                }
                helperText="Require phone verification for large parties"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* No-Show & Auto-Release */}
      <Card className="mb-6">
        <CardContent>
          <Typography variant="h6" className="mb-4">
            No-Show & Auto-Release
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Auto-Release After (minutes)"
                type="number"
                fullWidth
                value={settings.autoReleaseMinutes}
                onChange={(e) =>
                  handleChange('autoReleaseMinutes', parseInt(e.target.value))
                }
                helperText="Release table if customer doesn't arrive"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="No-Show Threshold (minutes)"
                type="number"
                fullWidth
                value={settings.noShowThresholdMinutes}
                onChange={(e) =>
                  handleChange('noShowThresholdMinutes', parseInt(e.target.value))
                }
                helperText="Mark as no-show after this time"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Reservation Lookahead (minutes)"
                type="number"
                fullWidth
                value={settings.reservationLookaheadMinutes ?? 120}
                onChange={(e) =>
                  handleChange('reservationLookaheadMinutes', parseInt(e.target.value))
                }
                helperText="How far ahead to show reserved tables on floor plan"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Pacing & Capacity (P1) */}
      <Card className="mb-6">
        <CardContent>
          <Typography variant="h6" className="mb-4">
            Pacing &amp; Capacity
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Turn Buffer (minutes)"
                type="number"
                fullWidth
                value={settings.tableBufferMinutes ?? 15}
                onChange={(e) =>
                  handleChange('tableBufferMinutes', parseInt(e.target.value) || 0)
                }
                helperText="Cleanup gap enforced between two sittings on the same table"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Max Covers per Slot"
                type="number"
                fullWidth
                value={settings.maxCoversPerSlot ?? ''}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  handleChange('maxCoversPerSlot', v === '' ? null : (parseInt(v) || null));
                }}
                helperText="Pacing cap on overlapping covers — leave blank for no cap"
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* SMS Notifications */}
      <Card className="mb-6">
        <CardContent>
          <Typography variant="h6" className="mb-4">
            SMS Notifications
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.sendConfirmationSms}
                    onChange={(e) =>
                      handleChange('sendConfirmationSms', e.target.checked)
                    }
                  />
                }
                label="Send Confirmation SMS"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.sendReminderSms}
                    onChange={(e) =>
                      handleChange('sendReminderSms', e.target.checked)
                    }
                  />
                }
                label="Send Reminder SMS"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                label="Reminder Hours Before"
                type="number"
                fullWidth
                value={settings.reminderHoursBefore}
                onChange={(e) =>
                  handleChange('reminderHoursBefore', parseInt(e.target.value))
                }
                disabled={!settings.sendReminderSms}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Save Button */}
      <Box className="flex justify-end">
        <Button
          variant="contained"
          size="large"
          startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={saving}
          sx={{
            backgroundColor: 'var(--action)',
            '&:hover': { backgroundColor: 'var(--action)' },
          }}
        >
          Save Settings
        </Button>
      </Box>
    </Box>
  );
};

export default ReservationSettings;
